// A small tag with an explanation on hover (desktop) or tap (phone) that works inside a card that is
// itself a button (the inventory card, the slot picker row): the tag stops the click from opening the
// card, and the tip is drawn in a portal under the tag and follows it on scroll. Krug 5 (K12.1) built
// it for the tier tag; 30. 9. 2026 it also explains PWR.

import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type PointerEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '@/lib/format';

export function InlineTip({
  children,
  tip,
  ariaLabel,
  className,
  tagProps,
  tipProps,
}: {
  children: ReactNode;
  tip: ReactNode;
  ariaLabel: string;
  className?: string;
  /** Extra attributes on the tag (`data-tier`, test ids). */
  tagProps?: Record<string, string | number | undefined>;
  /** Extra attributes on the tip (`data-tier-tip`). */
  tipProps?: Record<string, string | boolean | undefined>;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const tag = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLSpanElement>(null);
  const hover = useRef(false);
  const leave = useRef<number | undefined>(undefined);

  // The explanation sits under the tag and follows it when the page scrolls.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      if (!tag.current?.isConnected) return setOpen(false);
      const r = tag.current.getBoundingClientRect();
      const w = Math.min(260, window.innerWidth - 16);
      setPos({ left: Math.max(8, Math.min(window.innerWidth - w - 8, r.left + r.width / 2 - w / 2)), top: r.bottom + 6 });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      if (tag.current?.contains(e.target as Node) || tipRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const esc = (e: globalThis.KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', close);
    window.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', close);
      window.removeEventListener('keydown', esc);
    };
  }, [open]);

  const stop = (e: MouseEvent | PointerEvent | KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };
  return (
    <>
      <span
        ref={tag}
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        aria-expanded={open}
        {...tagProps}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          stop(e);
          // A click right after the mouse opened it keeps it open; a tap (no hover) toggles it.
          const keep = hover.current;
          hover.current = false;
          setOpen((o) => keep || !o);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            stop(e);
            setOpen((o) => !o);
          }
        }}
        onPointerEnter={(e) => {
          if (e.pointerType === 'mouse') {
            hover.current = true;
            setOpen(true);
          }
        }}
        onPointerLeave={(e) => {
          if (e.pointerType === 'mouse') {
            hover.current = false;
            // A moment to reach `Learn more` in the tip (29. 9. 2026).
            leave.current = window.setTimeout(() => setOpen(false), 200);
          }
        }}
        className={cx('mono cursor-help whitespace-nowrap underline decoration-dotted underline-offset-2', className)}
      >
        {children}
      </span>
      {open &&
        pos &&
        createPortal(
          <span
            ref={tipRef}
            role="tooltip"
            {...tipProps}
            onPointerEnter={() => window.clearTimeout(leave.current)}
            onPointerLeave={(e) => e.pointerType === 'mouse' && setOpen(false)}
            // The tip sits in a card (a button) in React's tree: its clicks stop here.
            onClick={(e) => e.stopPropagation()}
            className="fixed z-[120] flex w-[min(260px,calc(100vw-16px))] flex-col gap-1.5 rounded-[6px] border border-border bg-raised px-3 py-2 text-left font-sans text-[13px] normal-case leading-[18px] tracking-normal text-text shadow-pop"
            style={{ left: pos.left, top: pos.top }}
          >
            {tip}
          </span>,
          document.body,
        )}
    </>
  );
}
